import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';
import { PlusIcon, PencilIcon, TrashIcon, ArrowTopRightOnSquareIcon, BuildingStorefrontIcon, ComputerDesktopIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const AdminCenters = () => {
  const { isAdmin } = useUser();
  const [adminCenters, setAdminCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', url: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminCenters();
  }, []);

  const fetchAdminCenters = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAdminCenters();
      setAdminCenters(data);
    } catch (err) {
      setError('Failed to load admin centers.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', description: '', url: '' });
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description || '', url: item.url });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ name: '', description: '', url: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.url.trim()) {
      setError('Name and URL are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      if (editingItem) {
        await apiService.updateAdminCenter(editingItem.id, formData);
      } else {
        await apiService.createAdminCenter(formData);
      }
      await fetchAdminCenters();
      handleCancel();
    } catch (err) {
      setError('Failed to save admin center.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this admin center?')) return;
    try {
      await apiService.deleteAdminCenter(id);
      await fetchAdminCenters();
    } catch (err) {
      setError('Failed to delete admin center.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Centers</h1>
          <p className="mt-1 text-sm text-gray-500">Quick access to the admin portals your IT team uses.</p>
        </div>
        {isAdmin() && (
          <button
            onClick={handleAdd}
            className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Admin Center
          </button>
        )}
      </div>

      {isAdmin() && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Internal Tools</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: '/suppliers', icon: BuildingStorefrontIcon, iconBg: 'bg-amber-50', iconColor: 'text-amber-600', title: 'Suppliers', description: 'Manage supplier contracts, invoices and file attachments' },
              { to: '/assets', icon: ComputerDesktopIcon, iconBg: 'bg-green-50', iconColor: 'text-green-600', title: 'Asset Management', description: 'Track and manage IT hardware assets across the organisation' },
            ].map(({ to, icon: Icon, iconBg, iconColor, title, description }) => (
              <Link
                key={to}
                to={to}
                className="bg-white shadow rounded-lg p-5 hover:shadow-md transition-shadow flex items-center gap-4 group"
              >
                <div className={`p-2.5 rounded-full flex-shrink-0 ${iconBg}`}>
                  <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-sm">{title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{description}</p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {error && !showForm && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {showForm && (
        <div className="mb-6 bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingItem ? 'Edit Admin Center' : 'Add Admin Center'}
          </h2>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Microsoft 365 Admin Center"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="What is this admin center used for?"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://admin.microsoft.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingItem ? 'Update' : 'Add'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Admin Portals</h2>
      {adminCenters.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <p className="text-gray-500 text-sm">No admin centers added yet.</p>
          {isAdmin() && (
            <button
              onClick={handleAdd}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              Add your first admin center
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {adminCenters.map((item) => (
            <div key={item.id} className="bg-white shadow rounded-lg p-5 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-base font-semibold text-gray-900">{item.name}</h3>
                {isAdmin() && (
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-500 mb-4 flex-1">{item.description}</p>
              )}
              <div className="mt-auto pt-3">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Open
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1.5" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCenters;
